import { showGeoPermissionGuide } from '@/components/common/GlobalLocationPermissionGuide';
import { classifyGeoError } from '@/lib/geoPermission';
/**
 * Dialog premium para enviar localização no chat
 * 
 * Permite ao usuário inserir coordenadas manualmente ou
 * usar a localização atual do dispositivo
 * 
 * Design moderno com gradientes, bordas arredondadas e transições suaves
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Navigation, Loader2, ExternalLink, Send } from 'lucide-react';
import { toast } from 'sonner';

interface SendLocationDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Callback para enviar a localização */
  onSend: (location: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  }) => Promise<void>;
}

export function SendLocationDialog({
  open,
  onClose,
  onSend,
}: SendLocationDialogProps) {
  // Estados do formulário
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  
  // Estados de loading
  const [gettingLocation, setGettingLocation] = useState(false);
  const [sending, setSending] = useState(false);

  // Valida coordenadas
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const hasValidCoords = !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

  // Obtém localização atual do dispositivo
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Seu navegador não suporta geolocalização');
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toString());
        setLongitude(position.coords.longitude.toString());
        setName('Minha localização');
        setGettingLocation(false);
        toast.success('Localização obtida com sucesso');
      },
      (error) => {
        setGettingLocation(false);
        // v2026-10-22: guia global de permissão em vez de toast de erro.
        showGeoPermissionGuide(classifyGeoError(error.code));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Envia a localização
  const handleSend = async () => {
    if (!hasValidCoords) {
      toast.error('Informe coordenadas válidas');
      return;
    }

    setSending(true);
    try {
      await onSend({
        latitude: lat,
        longitude: lng,
        name: name.trim() || undefined,
        address: address.trim() || undefined,
      });
      handleClose();
    } catch (error) {
      console.error('Error sending location:', error);
      toast.error('Erro ao enviar localização');
    } finally {
      setSending(false);
    }
  };

  // Limpa e fecha o dialog
  const handleClose = () => {
    setLatitude('');
    setLongitude('');
    setName('');
    setAddress('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        {/* Header Premium com ícone grande e gradiente */}
        <DialogHeader className="space-y-4 pb-2">
          <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 shadow-lg shadow-emerald-500/25">
            <MapPin className="h-8 w-8 text-white" />
          </div>
          <div className="text-center space-y-1">
            <DialogTitle className="text-xl font-semibold">
              Enviar Localização
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Compartilhe sua localização atual ou digite coordenadas
            </p>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Botão premium de localização atual */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0 shadow-md shadow-emerald-500/20 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30"
            onClick={handleGetCurrentLocation}
            disabled={gettingLocation}
          >
            <div className="flex items-center gap-3">
              {gettingLocation ? (
                <div className="p-2 bg-white/20 rounded-full">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : (
                <div className="p-2 bg-white/20 rounded-full">
                  <Navigation className="h-5 w-5" />
                </div>
              )}
              <span className="font-medium">
                {gettingLocation ? 'Obtendo localização...' : 'Usar minha localização atual'}
              </span>
            </div>
          </Button>

          {/* Divisor estilizado */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-4 text-muted-foreground font-medium">
                ou digite manualmente
              </span>
            </div>
          </div>

          {/* Campos de coordenadas estilizados */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="latitude" className="text-sm font-medium flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">LAT</span>
                </div>
                Latitude
              </Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                placeholder="-23.5505"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                className="h-12 rounded-xl bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude" className="text-sm font-medium flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400">LNG</span>
                </div>
                Longitude
              </Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                placeholder="-46.6333"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                className="h-12 rounded-xl bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all"
              />
            </div>
          </div>

          {/* Nome do local estilizado */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <MapPin className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              </div>
              Nome do local
              <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="name"
              placeholder="Ex: Escritório, Casa, Loja..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 rounded-xl bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all"
            />
          </div>

          {/* Endereço estilizado */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                <ExternalLink className="h-3 w-3 text-teal-600 dark:text-teal-400" />
              </div>
              Endereço
              <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="address"
              placeholder="Ex: Av. Paulista, 1000 - São Paulo"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="h-12 rounded-xl bg-muted/50 border-0 focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all"
            />
          </div>

          {/* Preview premium da localização */}
          {hasValidCoords && (
            <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 transition-all duration-300">
              {/* Decoração de fundo */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-200/30 dark:bg-emerald-500/10 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-teal-200/30 dark:bg-teal-500/10 rounded-full blur-xl" />
              
              <div className="flex items-center gap-4 relative z-10">
                {/* Ícone com gradiente */}
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                
                {/* Informações */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {name || 'Localização'}
                  </p>
                  <p className="text-sm text-muted-foreground font-mono">
                    {lat.toFixed(6)}, {lng.toFixed(6)}
                  </p>
                  <a 
                    href={`https://www.google.com/maps?q=${lat},${lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 mt-1 font-medium"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Ver no Google Maps
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Premium */}
        <DialogFooter className="gap-3 sm:gap-3">
          <Button 
            variant="ghost" 
            onClick={handleClose} 
            disabled={sending}
            className="rounded-xl h-11"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={!hasValidCoords || sending}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md shadow-emerald-500/20 rounded-xl h-11 px-6 transition-all duration-300 hover:shadow-lg"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
